import { useState } from 'react';
import { QueryState } from '../components/page';
import { useIdentityFindings, useIdentityOverview } from '../api/hooks';
import type {
  IdentityFindingParams,
  IdentityFindingSource,
  IdentityFindingStatus,
  IdentityLevel,
} from '../api/types';
import { DataQualityHeader } from './DataQualityOverviewPage';
import {
  FINDING_KIND,
  FindingsTable,
  LEVEL_NAME,
  Labelled,
  PAGE_SIZE,
  Pager,
  control,
  findingKindText,
  loadError,
} from './medical-data-quality';

const STATUSES: { value: IdentityFindingStatus; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'dismissed', label: 'Dismissed' },
];

const SOURCES: { value: IdentityFindingSource; label: string }[] = [
  { value: 'rule', label: 'Rule checks' },
  { value: 'reviewer', label: 'Reviewers' },
  { value: 'ai', label: 'AI' },
];

/** Problems resolution cannot settle on its own, raised by rule checks, reviewers and the AI. */
export function FindingsPage() {
  const [params, setParams] = useState<IdentityFindingParams>({
    status: 'open',
    limit: PAGE_SIZE,
    offset: 0,
  });
  const findings = useIdentityFindings(params);
  const overview = useIdentityOverview();
  // AI findings name their own kinds, so offer the kinds that are open too.
  const kinds = [
    ...new Set([...Object.keys(FINDING_KIND), ...Object.keys(overview.data?.open_findings ?? {})]),
  ];
  const update = (next: IdentityFindingParams) =>
    setParams(previous => ({ ...previous, ...next, offset: 0 }));
  const page = findings.data;

  return (
    <div>
      <DataQualityHeader />
      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-3xl border bg-card p-4">
        <Labelled label="Status">
          <select
            aria-label="Status"
            className={control}
            value={params.status}
            onChange={event => update({ status: event.target.value as IdentityFindingStatus })}
          >
            {STATUSES.map(status => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </Labelled>
        <Labelled label="Kind">
          <select
            aria-label="Kind"
            className={control}
            value={params.kind ?? ''}
            onChange={event => update({ kind: event.target.value || undefined })}
          >
            <option value="">All kinds</option>
            {kinds.map(kind => (
              <option key={kind} value={kind}>
                {findingKindText(kind)}
              </option>
            ))}
          </select>
        </Labelled>
        <Labelled label="Source">
          <select
            aria-label="Source"
            className={control}
            value={params.source ?? ''}
            onChange={event =>
              update({
                source: (event.target.value || undefined) as IdentityFindingSource | undefined,
              })
            }
          >
            <option value="">All sources</option>
            {SOURCES.map(source => (
              <option key={source.value} value={source.value}>
                {source.label}
              </option>
            ))}
          </select>
        </Labelled>
        <Labelled label="Concerns">
          <select
            aria-label="Concerns"
            className={control}
            value={params.entity_kind ?? ''}
            onChange={event =>
              update({
                entity_kind: (event.target.value || undefined) as IdentityLevel | undefined,
              })
            }
          >
            <option value="">Everything</option>
            {(Object.keys(LEVEL_NAME) as IdentityLevel[]).map(level => (
              <option key={level} value={level}>
                {LEVEL_NAME[level].many}
              </option>
            ))}
          </select>
        </Labelled>
      </div>
      <QueryState isLoading={findings.isLoading} error={loadError(findings.error)}>
        {page?.items.length === 0 ? (
          <p className="rounded-3xl border p-6 text-sm text-muted-foreground">
            No Findings match these filters.
          </p>
        ) : (
          <FindingsTable findings={page?.items ?? []} />
        )}
        {page && (
          <Pager
            total={page.total}
            limit={page.limit}
            offset={page.offset}
            onChange={offset => setParams(previous => ({ ...previous, offset }))}
          />
        )}
      </QueryState>
    </div>
  );
}
